/**
 * Numbered Items v2 - Accordion Functionality
 * Huggah Theme Custom Section
 * 
 * Features:
 * - Desktop: Only one item open at a time (accordion)
 * - Mobile: Multiple items can be open (independent toggles)
 * - Auto-hide past events based on date
 * - Smooth animations and transitions
 * - Accessibility support (ARIA attributes, keyboard navigation)
 */

class NumberedItemsV2 {
  constructor(section) {
    console.log('=== NumberedItemsV2 constructor called ==='); // Debug
    console.log('Section:', section); // Debug
    this.section = section;
    
    // Prevent multiple initializations
    const alreadyInit = section.dataset.numberedItemsInitialized;
    console.log('Already initialized?', alreadyInit); // Debug
    
    if (alreadyInit === 'true') {
      console.log('Section already initialized, skipping'); // Debug
      return;
    }
    
    console.log('Setting initialized flag to true'); // Debug
    section.dataset.numberedItemsInitialized = 'true';
    
    const itemsNodeList = section.querySelectorAll('.numbered-items-v2__item');
    console.log('Found items NodeList:', itemsNodeList); // Debug
    this.items = Array.from(itemsNodeList);
    console.log('Items as array:', this.items); // Debug
    this.isMobile = window.innerWidth < 750;
    console.log('Is mobile?', this.isMobile); // Debug
    
    this.init();
  }
  
  init() {
    if (!this.items || this.items.length === 0) {
      console.error('No items found in init()'); // Debug
      return;
    }
    
    console.log('NumberedItemsV2 initialized with', this.items.length, 'items'); // Debug
    console.log('Items array:', this.items); // Debug
    
    // Set up click handlers for each item
    this.items.forEach((item, index) => {
      console.log('Processing item', index + 1, item); // Debug
      const header = item.querySelector('.numbered-items-v2__item-header');
      console.log('Found header for item', index + 1, ':', header); // Debug
      
      if (header) {
        console.log('Setting up click handler for item', index + 1); // Debug
        
        // Make entire tile clickable
        const clickHandler = (e) => {
          console.log('=== CLICK EVENT FIRED ==='); // Debug
          console.log('Event target:', e.target); // Debug
          console.log('Current target:', e.currentTarget); // Debug
          console.log('Handler attached to item', index + 1); // Debug
          console.log('Timestamp:', Date.now()); // Debug
          e.preventDefault();
          e.stopPropagation();
          console.log('Click detected on item', index + 1); // Debug
          console.log('Item classes before toggle:', item.className); // Debug
          this.toggleItem(item, e);
          console.log('Item classes after toggle:', item.className); // Debug
        };
        
        // Store reference to prevent duplicates
        if (header._numberedItemsClickHandler) {
          console.warn('Click handler already exists on item', index + 1, '- removing old one'); // Debug
          header.removeEventListener('click', header._numberedItemsClickHandler);
        }
        header._numberedItemsClickHandler = clickHandler;
        header.addEventListener('click', clickHandler);
        
        // Keyboard accessibility
        header.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleItem(item, e);
          }
        });
      } else {
        console.warn('No header found for item', index + 1); // Debug
      }
    });
    
    // Handle window resize (switch between desktop/mobile behavior)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 750;
        
        // If switching from desktop to mobile or vice versa, close all items
        if (wasMobile !== this.isMobile) {
          this.closeAllItems();
        }
      }, 250);
    });
    
    // Check and hide/dim past events
    this.checkPastEvents();
  }
  
  toggleItem(item, event) {
    console.log('Toggle item clicked!', item); // Debug
    
    const isActive = item.classList.contains('is-active');
    console.log('Item is currently active?', isActive); // Debug
    const header = item.querySelector('.numbered-items-v2__item-header');
    const content = item.querySelector('.numbered-items-v2__content');
    
    // Desktop accordion: If clicking an already-open item, just close it
    // If clicking a closed item, close others first then open this one
    if (!this.isMobile) {
      if (isActive) {
        // Just close this item (don't touch others)
        console.log('Item is open, closing it'); // Debug
        this.closeItem(item);
        if (header && content) {
          header.setAttribute('aria-expanded', 'false');
          content.setAttribute('aria-hidden', 'true');
        }
        return; // Done
      } else {
        // Close all other items, then open this one
        console.log('Item is closed, closing others and opening this one'); // Debug
        this.closeAllItems(item);
      }
    }
    
    // Mobile or opening an item on desktop
    if (isActive) {
      this.closeItem(item);
      console.log('Closing item (mobile)'); // Debug
    } else {
      this.openItem(item);
      console.log('Opening item'); // Debug
    }
    
    // Update ARIA attributes
    if (header && content) {
      const newState = !isActive;
      header.setAttribute('aria-expanded', newState);
      content.setAttribute('aria-hidden', !newState);
    }
  }
  
  openItem(item) {
    console.log('openItem() called for:', item); // Debug
    console.log('Classes before adding is-active:', item.className); // Debug
    item.classList.add('is-active');
    console.log('Classes after adding is-active:', item.className); // Debug
    
    // Smooth scroll to item on mobile
    if (this.isMobile) {
      setTimeout(() => {
        const itemTop = item.getBoundingClientRect().top + window.pageYOffset;
        const offset = 100; // Offset from top
        window.scrollTo({
          top: itemTop - offset,
          behavior: 'smooth'
        });
      }, 300);
    }
  }
  
  closeItem(item) {
    console.log('closeItem() called for:', item); // Debug
    console.log('Classes before removing is-active:', item.className); // Debug
    item.classList.remove('is-active');
    console.log('Classes after removing is-active:', item.className); // Debug
  }
  
  closeAllItems(exceptItem = null) {
    console.log('closeAllItems() called, except:', exceptItem); // Debug
    let closedCount = 0;
    this.items.forEach(item => {
      if (item !== exceptItem) {
        const wasActive = item.classList.contains('is-active');
        if (wasActive) {
          console.log('Closing item because it is not the exception:', item); // Debug
          closedCount++;
        }
        this.closeItem(item);
        
        // Update ARIA attributes
        const header = item.querySelector('.numbered-items-v2__item-header');
        const content = item.querySelector('.numbered-items-v2__content');
        if (header && content) {
          header.setAttribute('aria-expanded', 'false');
          content.setAttribute('aria-hidden', 'true');
        }
      } else {
        console.log('Skipping item because it is the exception:', item); // Debug
      }
    });
    console.log('Closed', closedCount, 'items'); // Debug
  }
  
  checkPastEvents() {
    const eventItems = this.section.querySelectorAll('.numbered-items-v2__subitem--event');
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    eventItems.forEach(eventItem => {
      const eventDateStr = eventItem.getAttribute('data-event-date');
      if (!eventDateStr) return;
      
      try {
        const eventDate = new Date(eventDateStr);
        eventDate.setHours(0, 0, 0, 0);
        
        if (eventDate < today) {
          // Event is in the past
          eventItem.classList.add('subitem--past-event');
          
          // Check if we should hide it completely
          const hideStyle = window.getComputedStyle(eventItem).display;
          if (hideStyle === 'none') {
            // Already hidden by inline style (hide_past_events setting)
            return;
          }
        } else {
          // Event is upcoming - ensure it's visible
          eventItem.classList.remove('subitem--past-event');
          eventItem.style.display = '';
        }
      } catch (error) {
        console.warn('Invalid event date format:', eventDateStr);
      }
    });
  }
}

// Initialize when DOM is ready - Multiple methods for reliability
function initNumberedItemsV2() {
  console.log('=== initNumberedItemsV2() called ==='); // Debug
  console.log('Document ready state:', document.readyState); // Debug
  console.log('Attempting to initialize NumberedItemsV2...'); // Debug
  
  const sections = document.querySelectorAll('.numbered-items-v2-section');
  console.log('Found', sections.length, 'numbered items sections'); // Debug
  console.log('Sections:', sections); // Debug
  
  if (sections.length === 0) {
    console.warn('No numbered-items-v2-section found in DOM'); // Debug
    console.log('All sections on page:', document.querySelectorAll('section')); // Debug
    console.log('All divs with class containing "numbered":', document.querySelectorAll('[class*="numbered"]')); // Debug
  }
  
  sections.forEach((section, index) => {
    console.log('Initializing section', index + 1); // Debug
    new NumberedItemsV2(section);
  });
}

// Debug helper - can be called from console
window.debugNumberedItems = function() {
  console.log('=== DEBUG INFO ===');
  console.log('Sections:', document.querySelectorAll('.numbered-items-v2-section'));
  console.log('Items:', document.querySelectorAll('.numbered-items-v2__item'));
  console.log('Headers:', document.querySelectorAll('.numbered-items-v2__item-header'));
  console.log('Script loaded:', typeof NumberedItemsV2 !== 'undefined');
};

// Multiple initialization approaches for reliability
if (document.readyState === 'loading') {
  console.log('DOM loading, waiting for DOMContentLoaded'); // Debug
  document.addEventListener('DOMContentLoaded', initNumberedItemsV2);
} else if (document.readyState === 'interactive' || document.readyState === 'complete') {
  console.log('DOM already ready, initializing immediately'); // Debug
  initNumberedItemsV2();
}

// Fallback: Initialize after a short delay if nothing else worked
setTimeout(() => {
  const sections = document.querySelectorAll('.numbered-items-v2-section');
  sections.forEach(section => {
    if (!section.dataset.numberedItemsInitialized || section.dataset.numberedItemsInitialized !== 'true') {
      console.log('Fallback initialization triggered'); // Debug
      new NumberedItemsV2(section);
    }
  });
}, 500);

// Re-initialize on Shopify theme editor changes
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.numbered-items-v2-section');
    if (section) {
      console.log('Shopify section load event triggered'); // Debug
      // Reset initialization flag to allow re-init
      section.dataset.numberedItemsInitialized = 'false';
      new NumberedItemsV2(section);
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.numbered-items-v2-section');
    if (section) {
      console.log('Shopify section unload event triggered'); // Debug
      section.dataset.numberedItemsInitialized = 'false';
    }
  });
}

